import React, { useEffect, useRef, useState, useCallback } from 'react';

export type RevealAnimation =
  | 'fade-up'
  | 'fade-down'
  | 'fade-in'
  | 'slide-left'
  | 'slide-right'
  | 'slide-from-left'
  | 'slide-from-right'
  | 'bounce-up'
  | 'pop-up'
  | 'scale-up'
  | 'zoom-in'
  | 'flip-up';

export interface ScrollRevealProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  animation?: RevealAnimation;
  delay?: number; // delay in ms
  duration?: number; // duration in ms
  distance?: number; // travel distance in px
  threshold?: number;
  once?: boolean; // defaults to false for continuous animations on scroll up & down
  className?: string;
  as?: React.ElementType;
}

export default function ScrollReveal({
  children,
  animation = 'fade-up',
  delay = 0,
  duration = 800,
  distance,
  threshold = 0.05,
  once = false,
  className = '',
  as: Component = 'div',
  style,
  ...props
}: ScrollRevealProps) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLElement | null>(null);

  // Robust viewport check using getBoundingClientRect
  const checkVisibility = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;

    // Element is in view when its top has entered viewport (with 30px trigger offset)
    // and its bottom hasn't completely left past the top
    const inView = rect.top < windowHeight - 30 && rect.bottom > 30;

    if (inView) {
      setIsVisible(true);
    } else if (!once) {
      // Out of view when completely below viewport or completely above viewport
      const completelyOutOfView = rect.bottom <= 0 || rect.top >= windowHeight;
      if (completelyOutOfView) {
        setIsVisible(false);
      }
    }
  }, [once]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const element = ref.current;
    if (!element) return;

    // 1. IntersectionObserver for high-performance reactive updates
    let observer: IntersectionObserver | null = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(true);
              if (once && observer) {
                observer.unobserve(entry.target);
              }
            } else if (!once) {
              setIsVisible(false);
            }
          });
        },
        {
          threshold,
          rootMargin: '0px 0px -30px 0px',
        }
      );
      observer.observe(element);
    }

    // 2. Continuous Scroll & Resize event listener with requestAnimationFrame
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          checkVisibility();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    // Initial check on mount
    const timer = setTimeout(() => {
      checkVisibility();
    }, 50);

    return () => {
      if (observer) observer.disconnect();
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [threshold, once, checkVisibility]);

  // Compute transform using GPU-accelerated translate3d
  const getTransform = () => {
    if (isVisible) return 'translate3d(0, 0, 0) scale(1)';
    const d = distance;
    switch (animation) {
      case 'fade-up':
        return `translate3d(0, ${d ?? 40}px, 0)`;
      case 'fade-down':
        return `translate3d(0, -${d ?? 40}px, 0)`;
      case 'bounce-up':
      case 'pop-up':
        // Spring / bounce pop up from bottom ("លោតចេញពីក្រោម")
        return `translate3d(0, ${d ?? 54}px, 0) scale(0.88)`;
      case 'slide-right':
      case 'slide-from-right':
        // Glide in clearly from the right side ("ចេញពីស្ដាំ")
        return `translate3d(${d ?? 80}px, 0, 0)`;
      case 'slide-left':
      case 'slide-from-left':
        // Glide in clearly from the left side
        return `translate3d(-${d ?? 80}px, 0, 0)`;
      case 'scale-up':
      case 'zoom-in':
        return `translate3d(0, ${d ?? 24}px, 0) scale(0.85)`;
      case 'flip-up':
        return `perspective(1000px) rotateX(15deg) translate3d(0, ${d ?? 40}px, 0)`;
      case 'fade-in':
      default:
        return 'translate3d(0, 0, 0)';
    }
  };

  // Dedicated easing per animation type
  const getTimingFunction = () => {
    switch (animation) {
      case 'bounce-up':
      case 'pop-up':
        // Energetic spring bounce with pleasant overshoot
        return 'cubic-bezier(0.34, 1.5, 0.64, 1)';
      case 'flip-up':
        return 'cubic-bezier(0.2, 0.8, 0.2, 1)';
      case 'slide-right':
      case 'slide-from-right':
      case 'slide-left':
      case 'slide-from-left':
      case 'fade-up':
      default:
        // Luxurious Apple/Linear deceleration
        return 'cubic-bezier(0.16, 1, 0.3, 1)';
    }
  };

  const dynamicStyles: React.CSSProperties = {
    opacity: isVisible ? 1 : 0,
    transform: getTransform(),
    filter: isVisible ? 'blur(0px)' : 'blur(4px)',
    transitionProperty: 'opacity, transform, filter',
    transitionDuration: isVisible ? `${duration}ms` : '150ms',
    transitionTimingFunction: getTimingFunction(),
    transitionDelay: isVisible ? `${delay}ms` : '0ms',
    willChange: 'opacity, transform, filter',
    ...style,
  };

  return (
    <Component
      ref={ref}
      style={dynamicStyles}
      className={className}
      {...props}
    >
      {children}
    </Component>
  );
}
